import { CheckIcon, LayoutDashboardIcon, TablePropertiesIcon, XIcon } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useT } from "../shell/i18n";
import { TableColumnEditor, useTablePersonalization } from "./table-personalization";
import type { ColumnPolicy } from "./table-presentation";
import type { TableSurfaceId } from "./table-surfaces";

/**
 * One control the platform adapter exposes for the current page.
 *
 * The registration contains presentation KEYS, never values or data-source names. A
 * surface can therefore offer an already-authorised field, but cannot turn a preference
 * into a projection or widen a row predicate. More adapters can join this union without
 * teaching the shell how a table, card grid or form implements its editor.
 */
export interface PersonalizableTable {
  readonly kind: "table";
  readonly id: TableSurfaceId;
  readonly label: string;
  readonly schemaRevision: number;
  readonly columns: readonly {
    readonly key: string;
    readonly header: string;
    readonly presentation?: ColumnPolicy;
  }[];
}

export interface PersonalizableList extends Omit<PersonalizableTable, "kind"> {
  readonly kind: "list";
}

export type PersonalizableSurface = PersonalizableTable | PersonalizableList;

interface Snapshot {
  readonly active: string | null;
  readonly editing: boolean;
  readonly surfaces: readonly PersonalizableSurface[];
}

interface Registration {
  surface: PersonalizableSurface;
  readonly instances: Set<PersonalizableSurface>;
}

function sameDefinition(left: PersonalizableSurface, right: PersonalizableSurface): boolean {
  return (
    left.kind === right.kind &&
    left.schemaRevision === right.schemaRevision &&
    left.columns.length === right.columns.length &&
    left.columns.every((column, index) => {
      const other = right.columns[index];
      return column.key === other?.key && column.presentation === other.presentation;
    })
  );
}

class PersonalizationStore {
  // One view can be drawn more than once on a page. `/workplaces` repeats the same
  // registration per province, for example; it is one user preference over several DOM
  // instances. Counting the instances keeps the remaining copies registered when one
  // group disappears, while `sameDefinition` refuses an accidental identity collision.
  private registrations = new Map<string, Registration>();
  private listeners = new Set<() => void>();
  private snapshot: Snapshot = { active: null, editing: false, surfaces: [] };

  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = () => this.snapshot;

  private publish(editing = this.snapshot.editing, requested = this.snapshot.active) {
    const surfaces = [...this.registrations.values()].map((registration) => registration.surface);
    const active = surfaces.some((surface) => surface.id === requested)
      ? requested
      : (surfaces[0]?.id ?? null);
    this.snapshot = { active, editing, surfaces };
    for (const listener of this.listeners) listener();
  }

  readonly register = (surface: PersonalizableSurface) => {
    const held = this.registrations.get(surface.id);
    if (held) {
      if (!sameDefinition(held.surface, surface)) {
        throw new Error(`table surface ${surface.id} was registered with two definitions`);
      }
      held.instances.add(surface);
    } else {
      this.registrations.set(surface.id, { surface, instances: new Set([surface]) });
    }
    this.publish();
    return () => {
      const current = this.registrations.get(surface.id);
      if (!current) return;
      current.instances.delete(surface);
      if (current.instances.size === 0) {
        this.registrations.delete(surface.id);
      } else if (current.surface === surface) {
        current.surface = current.instances.values().next().value as PersonalizableSurface;
      }
      this.publish();
    };
  };

  readonly start = () => this.publish(true);
  readonly stop = () => this.publish(false);
  readonly select = (id: string) => this.publish(this.snapshot.editing, id);
}

// Component tests and isolated documentation examples legitimately render a table without
// the product shell. Their registrations live only until React unmounts them; the real app
// always replaces this inert process-local fallback with its session-owned provider.
const fallback = new PersonalizationStore();
const PersonalizationContext = createContext<PersonalizationStore>(fallback);

export function PersonalizationProvider({ children }: { readonly children: ReactNode }) {
  const [store] = useState(() => new PersonalizationStore());
  return (
    <PersonalizationContext.Provider value={store}>{children}</PersonalizationContext.Provider>
  );
}

function useStore(): PersonalizationStore {
  return useContext(PersonalizationContext);
}

/** Stable commands for shell controls that must not subscribe to editing state. */
export function usePersonalizationController() {
  const store = useStore();
  return useMemo(() => ({ start: store.start, stop: store.stop, select: store.select }), [store]);
}

function useSnapshot(): Snapshot {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/**
 * Registers one adapter without subscribing the table to the whole registry.
 *
 * `fingerprint` makes an inline column declaration stable by meaning rather than by
 * array identity. Filtering, paging, or selecting a row can re-render a table without
 * repeatedly removing and adding its surface to the page editor.
 */
export function usePersonalizableTable(surface: PersonalizableTable | null) {
  return usePersonalizableSurface(surface);
}

export function usePersonalizableSurface(surface: PersonalizableSurface | null) {
  const store = useStore();
  const fingerprint = surface
    ? surface.columns
        .map((column) => `${column.key}:${column.header}:${column.presentation ?? "optional"}`)
        .join("|")
    : "";
  // The object is reconstructed from the primitives and the semantic column fingerprint.
  // Depending on `surface` itself would make an inline table declaration unregister and
  // register after every local filter or paging render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: semantic identity is fingerprinted
  const stable = useMemo(
    () =>
      surface
        ? {
            ...surface,
            columns: surface.columns.map((column) => ({ ...column })),
          }
        : null,
    [surface?.kind, surface?.id, surface?.label, surface?.schemaRevision, fingerprint],
  );

  useEffect(() => (stable ? store.register(stable) : undefined), [stable, store]);

  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return {
    active: surface !== null && snapshot.active === surface.id,
    editing: snapshot.editing,
    select: store.select,
  };
}

/**
 * The shell-level editing chrome. The page stays visible and readable underneath it;
 * registered adapters place an interception layer over only the controls being edited.
 */
export function PersonalizationChrome() {
  const t = useT();
  const store = useStore();
  const snapshot = useSnapshot();
  const tableStore = useTablePersonalization();
  if (!snapshot.editing) return null;

  const selected = snapshot.surfaces.find((surface) => surface.id === snapshot.active) ?? null;
  const stored = selected
    ? tableStore?.preference(selected.id, selected.schemaRevision)
    : undefined;

  return (
    <>
      <section
        aria-label={t("personalization.mode")}
        className="fixed top-(--size-topbar) right-0 left-0 z-(--layer-modal) flex min-h-12 flex-wrap items-center gap-3 border-line-default border-b bg-surface-shell px-4 py-2 text-on-shell shadow-(--shadow-overlay)"
      >
        <LayoutDashboardIcon className="size-4" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="m-0 text-shell-label font-medium">{t("personalization.mode")}</p>
          <p className="m-0 truncate text-shell-caption text-on-shell-muted">
            {t("personalization.modeHint")}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-on-shell hover:bg-surface-shell-hover hover:text-on-shell"
          onClick={store.stop}
        >
          <CheckIcon aria-hidden="true" />
          {t("personalization.done")}
        </Button>
      </section>

      <aside
        aria-label={t("personalization.panel")}
        className="fixed right-2 bottom-2 left-2 z-(--layer-modal) max-h-[60dvh] overflow-y-auto rounded-xl border border-line-default bg-surface-panel p-4 shadow-(--shadow-overlay) sm:top-28 sm:right-4 sm:bottom-4 sm:left-auto sm:w-80"
      >
        <header className="flex items-start gap-3 border-line-subtle border-b pb-3">
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-shell-title font-medium">
              {t("personalization.availableHeading")}
            </h2>
            <p className="m-0 mt-1 text-shell-caption text-muted-foreground">
              {t("personalization.availableHint")}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t("personalization.done")}
            onClick={store.stop}
          >
            <XIcon aria-hidden="true" />
          </Button>
        </header>

        {snapshot.surfaces.length === 0 ? (
          <p className="m-0 py-4 text-shell-body text-muted-foreground">
            {t("personalization.none")}
          </p>
        ) : (
          <div className="flex flex-col gap-4 pt-4">
            {snapshot.surfaces.length > 1 ? (
              <nav aria-label={t("personalization.surfaces")} className="flex flex-col gap-1">
                {snapshot.surfaces.map((surface) => (
                  <Button
                    key={surface.id}
                    type="button"
                    variant={surface.id === snapshot.active ? "secondary" : "ghost"}
                    size="sm"
                    className="w-full justify-start"
                    aria-current={surface.id === snapshot.active ? "true" : undefined}
                    onClick={() => store.select(surface.id)}
                  >
                    <TablePropertiesIcon aria-hidden="true" />
                    <span className="truncate">{surface.label}</span>
                  </Button>
                ))}
              </nav>
            ) : null}

            {selected ? (
              <section className="flex flex-col gap-3">
                <div>
                  <p className="m-0 text-shell-label font-medium">{selected.label}</p>
                  <p className="m-0 mt-1 text-shell-caption text-muted-foreground">
                    {t(
                      selected.kind === "table"
                        ? "personalization.tableHint"
                        : "personalization.listHint",
                    )}
                  </p>
                </div>
                <TableColumnEditor
                  key={stored?.belief_id ?? `${selected.id}-product-default`}
                  columns={selected.columns}
                  surfaceId={selected.id}
                  schemaRevision={selected.schemaRevision}
                />
              </section>
            ) : null}
          </div>
        )}
      </aside>
    </>
  );
}

export function PersonalizationTarget({
  active,
  label,
  onSelect,
}: {
  readonly active: boolean;
  readonly label: string;
  readonly onSelect: () => void;
}) {
  const t = useT();
  return (
    <button
      type="button"
      className={cn(
        "absolute inset-0 z-(--layer-overlay) cursor-pointer rounded-xl border-2 bg-transparent",
        active
          ? "border-focus ring-2 ring-focus-color"
          : "border-dashed border-line-strong hover:border-focus",
      )}
      aria-label={t("personalization.editSurface", { surface: label })}
      onClick={onSelect}
    />
  );
}
